import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  publishMultipleSecurityKeysDetectedMetric,
  publishSecurityKeyDetectionMetrics,
} from "@/apis/Metrics";
import { STEP_ID } from "@/components/constants";
import { useUsbDevices } from "@/hooks/useUsbDevices";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

import { findSecurityKeys } from "./helpers/security-key-utils";
import ReportSecurityKeyIssuesModal from "./ReportSecurityKeyIssuesModal";
import { SecurityKeyDetectionScreen } from "./SecurityKeyDetectionScreen";
import SecurityKeyInstructions from "./SecurityKeyInstructions";
import { SecurityKeyOtpInputScreen } from "./SecurityKeyOtpInputScreen";

/**
 * Represents the current state of the security key detection process.
 *
 * - `idle`: Initial state, waiting for user to insert security key
 * - `detecting`: Actively scanning for security key devices
 * - `detected`: Security key found, ready for OTP validation
 * - `timeout`: Detection timeout reached, showing error state
 */
type DetectionState = "idle" | "detecting" | "detected" | "timeout";

/**
 * Timeout duration for security key detection in milliseconds (15 seconds).
 * After this period, the detection process will transition to timeout state.
 */
const SECURITY_KEY_DETECTION_TIMEOUT_MS = 15 * 1000;

/**
 * Main component for the security key insertion and testing workflow step.
 * This component manages the entire process of inserting and testing a security key.
 *
 * @returns JSX element containing the security key testing workflow
 */
export default function InsertAndTestSecurityKey(): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const [detectionState, setDetectionState] = useState<DetectionState>("idle");
  const [hasMultipleKeys, setHasMultipleKeys] = useState(false);
  const [isInOtpErrorState, setIsInOtpErrorState] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const openModalRef = useRef<(() => void) | null>(null);

  const [detectionAttempts, setDetectionAttempts] = useState(0);
  const detectionStartTime = useRef<number>(0);

  const { devices, isLoading: usbIsLoading, error: usbError } = useUsbDevices();

  /**
   * Initializes chat support reference page for this security key workflow step
   * so users can get help specific to this workflow step when they open the
   * chat support interface.
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-insert-and-test-sec-key",
    });
  }, []);

  /**
   * Monitor USB devices for security key detection during active detection and after the initial timeout
   */
  useEffect(() => {
    if (
      (detectionState === "detecting" || detectionState === "timeout") &&
      !usbIsLoading &&
      !usbError
    ) {
      const securityKeys = findSecurityKeys(devices);

      if (securityKeys.length === 1) {
        const detectionTime = performance.now() - detectionStartTime.current;

        publishSecurityKeyDetectionMetrics({
          elapsedTime: detectionTime,
          attemptNumber: detectionAttempts,
        });

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
        setHasMultipleKeys(false);
        setDetectionState("detected");
      } else if (securityKeys.length > 1) {
        publishMultipleSecurityKeysDetectedMetric({
          securityKeys,
        });

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
        setHasMultipleKeys(true);
        setDetectionState("idle");
      }
    }
  }, [devices, detectionState, usbIsLoading, usbError, detectionAttempts]);

  /**
   * Cleanup timeout on component unmount to prevent memory leaks
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, []);

  /**
   * Starts the security key detection process with a timeout mechanism.
   *
   * Increments the detection attempt counter, records the start time for metrics,
   * and sets up a timeout that will transition to timeout state if no security
   * key is detected within the specified time period (15 seconds).
   */
  const handleDetectionStart = useCallback(() => {
    setDetectionAttempts((prev) => prev + 1);
    detectionStartTime.current = performance.now();

    setDetectionState("detecting");

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    timeoutRef.current = setTimeout(() => {
      setDetectionState("timeout");
    }, SECURITY_KEY_DETECTION_TIMEOUT_MS);
  }, []);

  /**
   * Handles back navigation by clearing any active timeouts and resetting to idle state.
   */
  const handleBack = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    setDetectionState("idle");
  }, []);

  /**
   * Handles the user's choice to continue without testing their security key.
   *
   * Proceeds to the next step in the onboarding workflow, allowing users to
   * continue with their onboarding process if they're unable to test their
   * security key for any reason.
   */
  const handleContinueWithoutTesting = useCallback(() => {
    dispatch({
      type: ActionType.NEXT_STEP,
      stepId: STEP_ID.INSERT_AND_TEST_SECURITY_KEY,
    });
  }, []);

  /**
   * Handles reporting security key issues by opening the reporting modal.
   *
   * Opens the security key issues reporting modal which allows users to
   * describe their problem and request assistance. The modal provides
   * options for different delivery methods and integrates with chat support.
   *
   * @param fromOtpError - Whether the report is being triggered from an OTP error state
   */
  const handleReportIssue = useCallback((fromOtpError = false): void => {
    setIsInOtpErrorState(Boolean(fromOtpError));
    openModalRef.current?.();
  }, []);

  /**
   * Renders the appropriate screen content based on current detection state and error conditions.
   *
   * Screen selection logic:
   * - USB error: Shows error screen with troubleshooting steps
   * - Detected: Shows OTP input screen for security key validation
   * - Detecting: Shows loading screen with detection progress
   * - Timeout: Shows OTP input screen with timeout error message
   * - Default (idle): Shows initial instructions screen
   *
   * @returns JSX element for the current screen state
   */
  const renderContent = () => {
    if (detectionState === "detected") {
      return (
        <SecurityKeyOtpInputScreen
          onBack={handleBack}
          detectionAttempts={detectionAttempts}
          onReportIssue={handleReportIssue}
          onContinueWithoutTesting={handleContinueWithoutTesting}
        />
      );
    }

    if (detectionState === "detecting") {
      return <SecurityKeyDetectionScreen />;
    }

    if (detectionState === "timeout") {
      return (
        <SecurityKeyOtpInputScreen
          onBack={handleBack}
          detectionAttempts={detectionAttempts}
          showDetectionTimeoutError={true}
          onReportIssue={handleReportIssue}
          onContinueWithoutTesting={handleContinueWithoutTesting}
        />
      );
    }

    return (
      <SecurityKeyInstructions
        onDetectionStart={handleDetectionStart}
        showMultipleKeysError={hasMultipleKeys}
        onContinueWithoutTesting={handleContinueWithoutTesting}
      />
    );
  };

  return (
    <ReportSecurityKeyIssuesModal
      shouldShowTimeoutContent={
        detectionState === "timeout" || isInOtpErrorState
      }
    >
      {({ open }) => {
        openModalRef.current = open;
        return renderContent();
      }}
    </ReportSecurityKeyIssuesModal>
  );
}
